/**
 * Mirrors: api/internal/httpapi/modules.go
 *
 *   GET /modules   which parts of this console exist
 *
 * A module is not a feature flag on a button. It decides whether a whole part
 * of the console EXISTS: its pages, its navigation, its API and the work behind
 * it. A console that shows an empty Posture screen has not been configured — it
 * has been broken — so the navigation and the routes both read this.
 */
import { withConsoleOrigin } from "./consoleOrigin";
import { coerceArray, get } from "@/lib/api";
import { tenantPath, tenantScopeKey } from "./tenantScope";

/** httpapi.ModuleSecurityScan. */
export const MODULE_SECURITY_SCAN = "security_scan";

/**
 * httpapi.ModuleIntune. The one module that ships DISABLED: it needs three
 * application permissions nothing else here uses, and an active Intune licence
 * no permission grant replaces.
 */
export const MODULE_INTUNE = "intune";

/**
 * httpapi.ModulePurview. Ships DISABLED for the same two reasons as Intune:
 * permissions nothing else here uses, and a Purview compliance licence no
 * permission grant replaces.
 */
export const MODULE_PURVIEW = "purview";

/**
 * The Defender family. Five separately LICENSED products under one navigation
 * heading, because that is how an operator thinks of them and how Microsoft
 * sells them: separately.
 */
export const MODULE_DEFENDER_OFFICE = "defender_office";
export const MODULE_DEFENDER_ENDPOINT = "defender_endpoint";
export const MODULE_DEFENDER_IDENTITY = "defender_identity";
export const MODULE_DEFENDER_CLOUD_APPS = "defender_cloud_apps";
export const MODULE_DEFENDER_XDR = "defender_xdr";

/**
 * httpapi.ModuleSharePoint. Ships ENABLED, unlike the other licensed modules:
 * the tenant-settings collector and its two checks were part of the product
 * before the module existed, and a default of off would silently drop them on
 * upgrade. Gated on the tenant's SharePoint or OneDrive service plans.
 */
export const MODULE_SHAREPOINT = "sharepoint";

/**
 * httpapi.ModuleTeams. Ships DISABLED like Intune: four application
 * permissions nothing else here uses, and a Teams licence no permission grant
 * replaces.
 */
export const MODULE_TEAMS = "teams";

/**
 * httpapi.ModuleAIBestPractice — AI best practice (Microsoft 365 & Azure), this
 * product's own framework built from Microsoft's published recommendations
 * (D20). Not a Microsoft framework. Ships DISABLED. It needs no Microsoft
 * licence, so its row carries no `licence`. With it off no AI or Azure
 * collector runs, no ai_bp check is evaluated, and no AI best practice score,
 * tab, report section or CSV exists: /posture/ai-bp and /reports/ai-bp.csv
 * answer 409 naming ai_best_practice_module_enabled. Module-owned checks are
 * never in the own score, on or off.
 */
export const MODULE_AI_BEST_PRACTICE = "ai_best_practice";

/**
 * httpapi's platform module. It is not a part of the console that can be
 * licensed or collected: it decides whether this deployment holds MORE THAN
 * ONE tenant at all, so it is scoped to the platform and is published by
 * GET /modules/platform rather than by the tenant list.
 */
export const MODULE_MULTITENANT = "multitenant";

/** modules.GroupDefender — the shared navigation heading. */
export const MODULE_GROUP_DEFENDER = "defender";

export const DEFENDER_MODULES = [
  MODULE_DEFENDER_OFFICE,
  MODULE_DEFENDER_ENDPOINT,
  MODULE_DEFENDER_IDENTITY,
  MODULE_DEFENDER_CLOUD_APPS,
  MODULE_DEFENDER_XDR,
] as const;

/**
 * The tenant's licence for a module, mirroring httpapi.ModuleLicence.
 *
 * `state` has THREE values, and the third is the point. `not_licensed` means
 * the subscriptions were read and none of them carries a service plan for this
 * module. `unknown` means no subscription has been read at all — nobody looked
 * — and it never disables anything. Collapsing the two would turn "the
 * collector has not run yet" into "you do not own this product".
 */
export interface ModuleLicence {
  state: "licensed" | "not_licensed" | "unknown";
  /** A full sentence naming the subscription and plan involved. */
  detail: string;
  /** What would satisfy the requirement, in Microsoft's own display names. */
  accepts: string[];
  matched_plan?: string;
  matched_sku?: string;
  /**
   * A plan the tenant holds that looks like the requirement and is not it —
   * "Mobile Device Management for Office 365" beside Intune, for instance.
   */
  near_miss?: string;
  subscriptions_read: number;
  /** True when the licence is what holds an ENABLED module off. */
  blocking: boolean;
}

/**
 * What this module still needs granted, from the same join as
 * GET /admin/access-status. Counted only for a module that needs access —
 * switched on and not held off by a missing licence.
 */
export interface ModuleAccess {
  /** Permissions and roles the probe found absent. */
  missing: number;
  /** Ones the probe could not measure: never counted as granted or missing. */
  unknown: number;
}

export interface AppModule {
  id: string;
  /** The EFFECTIVE state: what the navigation and the routes act on. */
  enabled: boolean;
  /**
   * What the operator chose, before the licence is considered. It differs from
   * `enabled` on a tenant with no licence, and a UI that had only the first
   * would show a switch that silently refuses to stay on.
   */
  configured: boolean;
  /** The app_settings key behind the switch, so the UI can point at it. */
  setting: string;
  title: string;
  summary: string;
  /** Absent for a module that needs no licence at all. */
  licence?: ModuleLicence;
  /** Navigation heading shared with sibling modules; absent when it has its own. */
  group?: string;
  /**
   * Whether the switch belongs to ONE TENANT or to the whole deployment.
   * A platform module is the same for every tenant and is edited on the
   * platform settings page; a tenant module is edited per tenant.
   */
  scope?: "tenant" | "platform";
  /**
   * Null when the server could not compute it (no credential, an unreadable
   * matrix) and absent on a platform module or an older server. Neither is
   * "nothing to grant".
   */
  access?: ModuleAccess | null;
}

export const moduleKeys = {
  all: () => ["modules", tenantScopeKey()] as const,
};

export async function fetchModules(): Promise<AppModule[]> {
  // Each module's access counts need the tenant-init planner, which needs the
  // console's public origin (see ./consoleOrigin).
  const raw = await get<{ items: AppModule[] | null }>(tenantPath("/modules"), withConsoleOrigin());
  return coerceArray(raw.items);
}
